/**
 * AI Exam Unlock Service
 * - Unlocks use testCountTracker.getTestCount() (all practice tests, including free 3).
 * - Thresholds: every 10 total tests (10, 20, 30, …). examNumber = floor(totalTests / 10).
 * - One active exam at a time; activeExamInstanceId is "1", "2", "3", … (legacy UUID ids still supported in storage).
 * - Completed exams are stored; retake from results (same instance id).
 *
 * IMPORTANT: Lazy getter for aiCoach was removed from unlock path; unlockAIExamForTesting seeds practice count only.
 */

import { testCountTracker } from './testCountTracker';

const STORAGE_PENDING_IDS = 'aiExamPendingInstanceIds';
const STORAGE_COMPLETED_IDS = 'aiExamCompletedInstanceIds';
const STORAGE_INSTANCE_DATA = 'aiExamInstanceData';
const STORAGE_NEW_UNLOCK_SEEN = 'aiExamNewUnlockSeen';

export const TOTAL_PRACTICE_TESTS = 26;

/** Practice test IDs — CBR 2026 structure (must match TestsPage / TEST_QUESTION_MAP). */
export const PRACTICE_TEST_IDS: readonly string[] = [
  // Tier 1: Critical (8)
  'road-markings',
  'motorway-rules',
  'traffic-signs',
  'vehicle-handling',
  'tram-interactions',
  'risk-awareness',
  'night-visibility',
  'adas-safety',
  // Tier 2: Important (9)
  'weather-conditions',
  'incident-first-aid',
  'pedestrian-crossings',
  'vehicle-categories',
  'construction-zones',
  'technology-safety',
  'fatigue-rest',
  'environmental-zones',
  'vehicle-documentation',
  // Tier 3: Enhancement (8)
  'priority-rules',
  'mandatory-signs',
  'traffic-lights-signals',
  'prohibitory-signs',
  'roundabout-rules',
  'emergency-procedures',
  'alcohol-drugs',
  'specialized-driving',
] as const;

/** Next total-test threshold after `examUnlockCount` completed AI exams: 10, 20, 30, … */
export function getUnlockThresholdForNextExam(examUnlockCount: number): number {
  return (examUnlockCount + 1) * 10;
}

export interface ExamInstanceData {
  instanceId: string;
  createdAt: string;
  examNumber: number;
  attemptCount: number;
  lastScore: number;
  lastTotalQuestions: number;
  lastPercentage: number;
  lastPassed: boolean;
  lastDate: string;
}

export interface ExamUnlockState {
  /** Total practice tests completed (testCountTracker; includes free tier tests in raw count when subscribed). */
  completedPracticeCount: number;
  /** Number of AI exam instances marked completed (any id, including legacy UUIDs). */
  examUnlockCount: number;
  activeExamInstanceId: string | null;
  isNewUnlock: boolean;
  practiceProgressText: string;
  progressMessage: string;
  nextUnlockPracticeNeeded: number | null;
  nextUnlockThreshold: number;
}

export interface NextUnlockRequirement {
  message: string;
  practiceNeeded: number;
}

function getStoredPendingIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_PENDING_IDS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setStoredPendingIds(ids: string[]): void {
  localStorage.setItem(STORAGE_PENDING_IDS, JSON.stringify(ids));
}

function getStoredCompletedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_COMPLETED_IDS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setStoredCompletedIds(ids: string[]): void {
  localStorage.setItem(STORAGE_COMPLETED_IDS, JSON.stringify(ids));
}

function getStoredInstanceData(): Record<string, ExamInstanceData> {
  try {
    const raw = localStorage.getItem(STORAGE_INSTANCE_DATA);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function setStoredInstanceData(data: Record<string, ExamInstanceData>): void {
  localStorage.setItem(STORAGE_INSTANCE_DATA, JSON.stringify(data));
}

function getNewUnlockSeenId(): string | null {
  return localStorage.getItem(STORAGE_NEW_UNLOCK_SEEN);
}

function setNewUnlockSeenId(instanceId: string): void {
  localStorage.setItem(STORAGE_NEW_UNLOCK_SEEN, instanceId);
}

function isNumericExamId(id: string): boolean {
  return /^\d+$/.test(id);
}

/** Remove completed ids from pending; keep at most one active pending. */
function normalizePendingList(): string[] {
  const raw = getStoredPendingIds();
  const completed = new Set(getStoredCompletedIds());
  let pending = raw.filter((id) => !completed.has(id));
  if (pending.length > 1) {
    pending = [pending[0]];
  }
  const prev = JSON.stringify(raw);
  const next = JSON.stringify(pending);
  if (prev !== next) {
    setStoredPendingIds(pending);
  }
  return pending;
}

/**
 * Unlock at most ONE new exam if total tests cross the next threshold.
 * Call when evaluating state (e.g. dashboard / tests page load).
 */
export function maybeUnlockOneExam(): string | null {
  normalizePendingList();
  const pending = getStoredPendingIds();
  if (pending.length > 0) {
    return null;
  }

  const totalTests = testCountTracker.getTestCount();
  const examNumber = Math.floor(totalTests / 10);
  const numCompleted = getStoredCompletedIds().length;

  if (examNumber <= numCompleted) {
    return null;
  }

  const instanceId = String(examNumber);
  const instanceData: ExamInstanceData = {
    instanceId,
    createdAt: new Date().toISOString(),
    examNumber,
    attemptCount: 0,
    lastScore: 0,
    lastTotalQuestions: 0,
    lastPercentage: 0,
    lastPassed: false,
    lastDate: '',
  };

  const data = getStoredInstanceData();
  data[instanceId] = instanceData;
  setStoredInstanceData(data);
  setStoredPendingIds([instanceId]);
  return instanceId;
}

export function getExamUnlockState(): ExamUnlockState {
  const newlyUnlocked = maybeUnlockOneExam();

  const totalTests = testCountTracker.getTestCount();
  normalizePendingList();
  const examUnlockCount = getStoredCompletedIds().length;
  const pending = getStoredPendingIds();
  const activeExamInstanceId = pending.length > 0 ? pending[0] : null;

  const lastSeenId = getNewUnlockSeenId();
  const isNewUnlock =
    activeExamInstanceId !== null &&
    activeExamInstanceId === newlyUnlocked &&
    activeExamInstanceId !== lastSeenId;

  const practiceProgressText = `${totalTests} practice tests completed`;
  const nextUnlockThreshold = getUnlockThresholdForNextExam(examUnlockCount);

  let progressMessage: string;
  let nextUnlockPracticeNeeded: number | null = null;

  if (activeExamInstanceId !== null) {
    progressMessage = `Next AI exam unlocks after ${nextUnlockThreshold} total practice tests.`;
  } else {
    const needed = Math.max(0, nextUnlockThreshold - totalTests);
    progressMessage =
      needed > 0
        ? `Complete ${needed} more practice test${needed === 1 ? '' : 's'} to unlock your next AI exam`
        : 'Next AI exam is ready.';
    nextUnlockPracticeNeeded = needed > 0 ? needed : null;
  }

  return {
    completedPracticeCount: totalTests,
    examUnlockCount,
    activeExamInstanceId,
    isNewUnlock,
    practiceProgressText,
    progressMessage,
    nextUnlockPracticeNeeded,
    nextUnlockThreshold,
  };
}

export function getPreUnlockMessage(): string {
  const totalTests = testCountTracker.getTestCount();
  const numCompleted = getStoredCompletedIds().length;
  const nextThreshold = getUnlockThresholdForNextExam(numCompleted);
  const needed = Math.max(0, nextThreshold - totalTests);
  return `Complete ${needed} more practice test${needed === 1 ? '' : 's'} to unlock your AI Exam`;
}

export function getNextUnlockDisplayMessage(): string | null {
  normalizePendingList();
  if (getStoredPendingIds().length > 0) return null;
  const totalTests = testCountTracker.getTestCount();
  const numCompleted = getStoredCompletedIds().length;
  const nextThreshold = getUnlockThresholdForNextExam(numCompleted);
  const needed = nextThreshold - totalTests;
  if (needed <= 0) return null;
  return `Complete ${needed} more practice test${needed === 1 ? '' : 's'} to unlock your next AI Exam`;
}

export function getNextUnlockRequirement(): NextUnlockRequirement | null {
  normalizePendingList();
  if (getStoredPendingIds().length > 0) return null;
  const totalTests = testCountTracker.getTestCount();
  const numCompleted = getStoredCompletedIds().length;
  const nextThreshold = getUnlockThresholdForNextExam(numCompleted);
  const needed = nextThreshold - totalTests;
  if (needed <= 0) return null;
  return {
    message: `${needed} more practice test${needed === 1 ? '' : 's'} to unlock the next AI exam`,
    practiceNeeded: needed,
  };
}

export function markNewUnlockSeen(instanceId: string): void {
  if (instanceId) setNewUnlockSeenId(instanceId);
}

export function recordRetakeAttempt(
  instanceId: string,
  score: number,
  totalQuestions: number,
  passed: boolean,
  date?: string
): void {
  const completed = getStoredCompletedIds();
  if (!completed.includes(instanceId)) return;

  const data = getStoredInstanceData();
  const existing = data[instanceId];
  if (!existing) return;

  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
  const updated: ExamInstanceData = {
    ...existing,
    attemptCount: existing.attemptCount + 1,
    lastScore: score,
    lastTotalQuestions: totalQuestions,
    lastPercentage: percentage,
    lastPassed: passed,
    lastDate: date || new Date().toISOString(),
  };
  data[instanceId] = updated;
  setStoredInstanceData(data);
}

export function getLastCompletedInstanceId(): string | null {
  const history = getExamHistory();
  return history.length > 0 ? history[0].examInstanceId : null;
}

export function completeExamInstance(
  instanceId: string,
  score: number,
  totalQuestions: number,
  passed: boolean,
  date?: string
): void {
  const pending = getStoredPendingIds();
  const index = pending.indexOf(instanceId);
  if (index < 0) return;

  const data = getStoredInstanceData();
  const existing = data[instanceId] || {
    instanceId,
    createdAt: new Date().toISOString(),
    examNumber: isNumericExamId(instanceId) ? parseInt(instanceId, 10) : 0,
    attemptCount: 0,
    lastScore: 0,
    lastTotalQuestions: 0,
    lastPercentage: 0,
    lastPassed: false,
    lastDate: '',
  };

  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
  const updated: ExamInstanceData = {
    ...existing,
    attemptCount: existing.attemptCount + 1,
    lastScore: score,
    lastTotalQuestions: totalQuestions,
    lastPercentage: percentage,
    lastPassed: passed,
    lastDate: date || new Date().toISOString(),
  };
  data[instanceId] = updated;
  setStoredInstanceData(data);

  const newPending = pending.filter((id) => id !== instanceId);
  setStoredPendingIds(newPending);
  setStoredCompletedIds([...getStoredCompletedIds(), instanceId]);
}

export function getActiveExamInstanceId(): string | null {
  normalizePendingList();
  const pending = getStoredPendingIds();
  return pending.length > 0 ? pending[0] : null;
}

export interface ExamHistoryEntry {
  examInstanceId: string;
  examNumber: number;
  attemptCount: number;
  score: number;
  totalQuestions: number;
  percentage: number;
  passed: boolean;
  date: string;
}

export function getExamHistory(): ExamHistoryEntry[] {
  const completedIds = getStoredCompletedIds();
  const data = getStoredInstanceData();
  const entries: ExamHistoryEntry[] = completedIds
    .map((id) => {
      const d = data[id];
      if (!d || d.attemptCount === 0) return null;
      return {
        examInstanceId: d.instanceId,
        examNumber: d.examNumber,
        attemptCount: d.attemptCount,
        score: d.lastScore,
        totalQuestions: d.lastTotalQuestions,
        percentage: d.lastPercentage,
        passed: d.lastPassed,
        date: d.lastDate,
      };
    })
    .filter((e): e is ExamHistoryEntry => e !== null);
  entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return entries;
}

export function getInstanceData(instanceId: string): ExamInstanceData | null {
  return getStoredInstanceData()[instanceId] || null;
}

export function isInstanceCompleted(instanceId: string): boolean {
  return getStoredCompletedIds().includes(instanceId);
}

export function getActiveExamDisplayNumber(): number | null {
  normalizePendingList();
  const pending = getStoredPendingIds();
  if (pending.length === 0) return null;
  const data = getStoredInstanceData()[pending[0]];
  return data ? data.examNumber : getStoredCompletedIds().length + 1;
}

export function invalidateCache(): void {
  // No in-memory cache; storage is source of truth.
}

const STORAGE_INSTANCE_QUESTIONS_PREFIX = 'aiExamInstanceQuestions_';

export function getCachedQuestionsForInstance(instanceId: string): unknown[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_INSTANCE_QUESTIONS_PREFIX + instanceId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setCachedQuestionsForInstance(instanceId: string, questions: unknown[]): void {
  try {
    localStorage.setItem(STORAGE_INSTANCE_QUESTIONS_PREFIX + instanceId, JSON.stringify(questions));
  } catch {
    // ignore
  }
}

/**
 * Dev helper: set total practice count to 10 so first AI exam can unlock (subscribed users).
 */
export function unlockAIExamForTesting(): void {
  try {
    localStorage.setItem(
      'practiceTestCount',
      JSON.stringify({
        count: 10,
        lastUpdated: new Date().toISOString(),
      })
    );
  } catch {
    // ignore
  }
  testCountTracker.invalidateCache();
}
